import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { 
  MessageCircle, X, Send, Bot, User, Loader2, 
  ChevronDown, HelpCircle, ShoppingCart, CreditCard, 
  Users, Shield, Package
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';
import { getBackendUrl } from '@/utils/backendUrl';

const API_URL = getBackendUrl();

// FAQ Data for quick questions
const faqCategories = [
  {
    id: 'buying',
    label: 'Buying',
    icon: ShoppingCart,
    color: '#00F0FF',
    questions: [
      { q: "How do I purchase a product on theailedger.ai?", short: "How to buy?" },
      { q: "How do I access my purchased products on theailedger.ai?", short: "Access purchases" },
      { q: "What is theailedger.ai's refund policy?", short: "Refund policy" }
    ]
  },
  {
    id: 'selling',
    label: 'Selling',
    icon: CreditCard,
    color: '#00F0FF',
    questions: [
      { q: "How do I become a seller on theailedger.ai?", short: "Become a seller" },
      { q: "What fees does theailedger.ai charge sellers?", short: "Platform fees" },
      { q: "How do I receive payments as a seller on theailedger.ai?", short: "Get paid" }
    ]
  },
  {
    id: 'affiliate',
    label: 'Affiliate',
    icon: Users,
    color: '#00F0FF',
    questions: [
      { q: "How does the affiliate program work on theailedger.ai?", short: "Affiliate program" },
      { q: "How do I become an affiliate on theailedger.ai as a buyer?", short: "Join as affiliate" },
      { q: "When do I get paid as an affiliate on theailedger.ai?", short: "Affiliate payouts" }
    ]
  },
  {
    id: 'account',
    label: 'Account',
    icon: Shield,
    color: '#00FF94',
    questions: [
      { q: "How do I reset my password on theailedger.ai?", short: "Reset password" },
      { q: "Is my payment information secure on theailedger.ai?", short: "Payment security" }
    ]
  }
];

function FloatingChatbot() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showFAQs, setShowFAQs] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [chatMessages, setChatMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! 👋 I'm your AI assistant. How can I help you today? Browse FAQs below or type your question!"
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSendMessage = async (message) => {
    const userMessage = message || inputMessage.trim();
    if (!userMessage || isTyping) return;

    setInputMessage('');
    setShowFAQs(false);
    setSelectedCategory(null);
    
    // Add user message
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsTyping(true);

    try {
      const response = await axios.post(`${API_URL}/api/support/chat`, {
        message: userMessage,
        session_id: sessionId
      });

      setSessionId(response.data.session_id);
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.message }]);
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I'm having trouble right now. Please try again or email support@theailedger.ai" 
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleQuickQuestion = (question) => {
    handleSendMessage(question);
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Reset to show FAQs when opening
      setShowFAQs(true);
      setSelectedCategory(null);
    }
  };

  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-36 right-6 w-96 max-w-[calc(100vw-48px)] bg-neutral-900 border border-white/10 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#00F0FF]/20 to-white/20 p-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00F0FF] to-white flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-white">AI Support</h3>
                <p className="text-xs text-neutral-400">Always here to help</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleChat}
              className="text-neutral-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[400px] min-h-[300px]">
            {chatMessages.map((msg, idx) => (
              <div
                key={msg.id || msg.timestamp || `msg-${idx}-${msg.role}-${(msg.content || '').slice(0, 12)}`}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-[#00F0FF] text-black'
                      : 'bg-neutral-800 text-white'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-neutral-800 rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-[#00F0FF]" />
                    <span className="text-sm text-neutral-400">Typing...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* FAQ Section */}
          {showFAQs && !isTyping && (
            <div className="border-t border-white/10 p-3 bg-neutral-900/50">
              <div className="flex items-center gap-2 mb-3">
                <HelpCircle className="w-4 h-4 text-[#00F0FF]" />
                <span className="text-xs font-semibold text-neutral-400">Quick Questions</span>
              </div>
              
              {!selectedCategory ? (
                <div className="grid grid-cols-2 gap-2">
                  {faqCategories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className="flex items-center gap-2 p-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 transition-colors text-left"
                    >
                      <cat.icon className="w-4 h-4" style={{ color: cat.color }} />
                      <span className="text-xs text-white">{cat.label}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className="flex items-center gap-1 text-xs text-[#00F0FF] hover:underline mb-2"
                  >
                    <ChevronDown className="w-3 h-3 rotate-90" />
                    Back to categories
                  </button>
                  {faqCategories.find(c => c.id === selectedCategory)?.questions.map((q, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleQuickQuestion(q.q)}
                      className="w-full text-left p-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 transition-colors text-xs text-white"
                    >
                      {q.short}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Input Area */}
          <div className="p-3 border-t border-white/10">
            <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }} className="flex gap-2">
              <Input
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-neutral-800 border-white/10 text-white text-sm"
                disabled={isTyping}
              />
              <Button
                type="submit"
                disabled={!inputMessage.trim() || isTyping}
                className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Floating Button - positioned above Emergent badge */}
      <button
        onClick={toggleChat}
        className={`fixed bottom-20 right-6 w-14 h-14 rounded-full shadow-lg z-50 flex items-center justify-center transition-all duration-300 ${
          isOpen 
            ? 'bg-neutral-800 hover:bg-neutral-700' 
            : 'bg-gradient-to-r from-[#00F0FF] to-white hover:opacity-90'
        }`}
      >
        {isOpen ? (
          <ChevronDown className="w-6 h-6 text-white" />
        ) : (
          <>
            <MessageCircle className="w-6 h-6 text-white" />
            {/* Pulse animation */}
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#00F0FF] rounded-full animate-pulse" />
          </>
        )}
      </button>
    </>
  );
}

export default FloatingChatbot;
